import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  ChevronRight,
  LayoutDashboard,
  UtensilsCrossed,
  Bot,
  BarChart,
  Settings,
  LogOut,
  BarChart2,
  Package,
  Handshake,
  DollarSign,
  Recycle,
  Calendar,
  Lightbulb,
  ChefHat,
  LineChart,
  Menu,
  Building2,
  BarChart3
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useAdmin } from '@/contexts/AdminContext';

const SIDEBAR_WIDTH = 240;
const COLLAPSED_WIDTH = 70;

interface SidebarProps {
  onToggle?: (expanded: boolean) => void;
}

const Sidebar = ({ onToggle }: SidebarProps) => {
  const [expanded, setExpanded] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if it's a mobile device
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Toggle sidebar for desktop
  const toggleSidebar = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    // Call the onToggle prop if provided
    if (onToggle) {
      onToggle(newExpanded);
    }
  };

  // Toggle sidebar for mobile
  const toggleMobileSidebar = () => {
    setMobileOpen(!mobileOpen);
  };

  // Handle navigation
  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    logout();
  };

  // Update isMobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile && mobileOpen) {
        setMobileOpen(false);
      }
      if (mobile) {
        setExpanded(false);
        // Call the onToggle prop if provided
        if (onToggle) {
          onToggle(false);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initialize on mount

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [mobileOpen, onToggle]);

  // Close mobile sidebar when changing routes
  useEffect(() => {
    if (mobileOpen) {
      setMobileOpen(false);
    }
  }, [location.pathname]);

  const { logout } = useAuth();
  const { isFeatureEnabled } = useAdmin();

  // Menu items configuration with added id property
  const { t } = useTranslation();

  const menuItems = [
    {
      id: 'dashboard',
      text: t('nav.dashboard'),
      icon: <LayoutDashboard size={22} />,
      path: '/dashboard',
    },
    {
      id: 'companies',
      text: t('nav.companies'),
      icon: <Building2 size={22} />,
      path: '/companies',
    },
    {
      id: 'recipes',
      text: t('nav.recipes'),
      icon: <UtensilsCrossed size={22} />,
      path: '/recipes',
    },
    {
      id: 'menu-engineering',
      text: t('nav.menuPerformance'),
      icon: <BarChart2 size={22} />,
      path: '/menu-engineering',
    },
    {
      id: 'inventory-intelligence',
      text: t('nav.inventoryIntelligence'),
      icon: <Package size={22} />,
      path: '/inventory-intelligence',
    },
    {
      id: 'supplier-relationship',
      text: t('nav.supplierRelationship'),
      icon: <Handshake size={22} />,
      path: '/supplier-relationship',
    },
    {
      id: 'dynamic-pricing',
      text: t('nav.dynamicPricing'),
      icon: <DollarSign size={22} />,
      path: '/dynamic-pricing',
    },
    {
      id: 'waste-reduction',
      text: t('nav.wasteReduction'),
      icon: <Recycle size={22} />,
      path: '/waste-reduction',
    },
    {
      id: 'procurement-calendar',
      text: t('nav.procurementCalendar'),
      icon: <Calendar size={22} />,
      path: '/procurement-calendar',
    },
    {
      id: 'recipe-innovation',
      text: t('nav.recipeInnovation'),
      icon: <Lightbulb size={22} />,
      path: '/recipe-innovation',
    },
    {
      id: 'operational-efficiency',
      text: t('nav.operationalEfficiency'),
      icon: <ChefHat size={22} />,
      path: '/operational-efficiency',
    },
    {
      id: 'financial-performance',
      text: t('nav.financialPerformance'),
      icon: <LineChart size={22} />,
      path: '/financial-performance',
    },
    {
      id: 'ai-assistant',
      text: t('nav.aiAssistant'),
      icon: <Bot size={22} />,
      path: '/ai-assistant',
    },
    {
      id: 'analysis',
      text: 'Análisis',
      icon: <BarChart3 size={22} />,
      path: '/analysis',
    },
    {
      id: 'reports',
      text: t('nav.reports'),
      icon: <BarChart size={22} />,
      path: '/reports',
    },
    {
      id: 'settings',
      text: t('nav.settings'),
      icon: <Settings size={22} />,
      path: '/settings',
    },
  ];

  // Filter out disabled menu items
  const enabledMenuItems = menuItems.filter(item => isFeatureEnabled(item.id));

  // Mobile menu button
  const MobileMenuButton = () => (
    <button
      onClick={toggleMobileSidebar}
      className="fixed top-4 left-4 p-2 z-50 rounded-full bg-primary/20 text-primary md:hidden"
      aria-label="Toggle mobile menu"
    >
      <Menu size={24} />
    </button>
  );

  return (
    <>
      <MobileMenuButton />
      
      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/70 z-40 md:hidden backdrop-blur-sm" 
          onClick={toggleMobileSidebar}
        />
      )}
      
      <aside 
        className={cn(
          "h-screen fixed left-0 top-0 z-40 flex flex-col bg-sidebar border-r border-sidebar-border",
          "transition-all duration-300 ease-in-out",
          isMobile 
            ? "transform lg:transform-none" 
            : expanded ? `w-[${SIDEBAR_WIDTH}px]` : `w-[${COLLAPSED_WIDTH}px]`,
          isMobile && (mobileOpen 
            ? "translate-x-0" 
            : "-translate-x-full"),
          !isMobile && (expanded 
            ? `lg:w-[${SIDEBAR_WIDTH}px]` 
            : `lg:w-[${COLLAPSED_WIDTH}px]`)
        )}
        style={{ 
          width: isMobile 
            ? SIDEBAR_WIDTH 
            : (expanded ? SIDEBAR_WIDTH : COLLAPSED_WIDTH) 
        }}
      >
        <div className="flex items-center justify-between px-4 h-16 border-b border-sidebar-border">
          <div className={cn(
            "flex items-center overflow-hidden transition-opacity duration-300",
            (expanded || isMobile) ? "opacity-100" : "opacity-0",
            // Ensure logo is centered on mobile view to avoid overlap with hamburger
            isMobile && "justify-center w-full"
          )}>
            <span className="font-bold text-xl text-primary">MrCook</span>
          </div>
          {!isMobile && (
            <button 
              onClick={toggleSidebar}
              className="p-1.5 rounded-full hover:bg-primary/10 transition-colors"
              aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
            >
              {expanded ? 
                <ChevronLeft size={20} className="text-primary" /> : 
                <ChevronRight size={20} className="text-primary" />
              }
            </button>
          )}
          {isMobile && (
            <button 
              onClick={toggleMobileSidebar}
              className="p-1.5 rounded-full hover:bg-primary/10 transition-colors absolute right-2"
              aria-label="Close sidebar"
            >
              <ChevronLeft size={20} className="text-primary" />
            </button>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-2">
            {enabledMenuItems.map((item) => {
              const isActive = location.pathname === item.path;
              
              return (
                <li key={item.text}>
                  <button
                    onClick={() => handleNavigate(item.path)}
                    className={cn(
                      "flex items-center w-full px-3 py-2.5 rounded-lg transition-all duration-200",
                      "hover:bg-primary/10",
                      isActive ? "bg-primary/20 text-primary font-medium" : "text-foreground/80"
                    )}
                  >
                    <span className={cn(isActive ? "text-primary" : "text-foreground/80")}>{item.icon}</span>
                    <span className={cn(
                      "ml-3 whitespace-nowrap transition-opacity duration-300",
                      (expanded || isMobile) ? "opacity-100" : "opacity-0",
                      isActive ? "text-primary font-medium" : "text-foreground/80"
                    )}>
                      {item.text}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="px-2 py-4 border-t border-sidebar-border">
          <button
            onClick={handleLogout}
            className={cn(
              "flex items-center w-full px-3 py-2.5 rounded-lg text-destructive transition-all duration-200",
              "hover:bg-destructive/10"
            )}
          >
            <LogOut size={22} />
            <span className={cn(
              "ml-3 whitespace-nowrap transition-opacity duration-300",
              (expanded || isMobile) ? "opacity-100" : "opacity-0"
            )}>
              {t('nav.logout')}
            </span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
